import React, { lazy, useEffect, useMemo, useState } from 'react';

import styled from 'styled-components';

import { StompSocketState } from '@stomp/stompjs';
import { Navigate, Route, Routes } from 'react-router-dom';

import UILoader from '@aquariuxfintech/aqx-trader-react-ui/loader';

import { APP_ROUTES, SECURED_ROUTES } from '@aqx-core/common/constants/router';
import { useAccountSwitchedSnackbar } from '@aqx-core/common/hooks/switch-account/use-account-switch-snackbar';
import { useAccountDetails } from '@aqx-core/common/services/auth';
import { useCompanyAccess } from '@aqx-core/common/services/company/hooks';
import { socketService } from '@aqx-core/common/socket';

const AssetsScreen = lazy(() => import('./assets'));
const DealerScreen = lazy(() => import('./dealer'));
const MainScreen = lazy(() => import('./main'));
const MarketsScreen = lazy(() => import('./markets'));
const SignalScreen = lazy(() => import('./signal'));
const TradeScreen = lazy(() => import('./trade'));
const NewsScreen = lazy(() => import('./news'));
const CalendarScreen = lazy(() => import('./calendar'));
const EducationScreen = lazy(() => import('./education'));

const SecuredRoutes: FCC = () => {
  const [connected, setConnected] = useState(false);

  const { hasDealerAccess, hasNewsAccess, hasEducationalVideo, hasCalendarAccess } = useCompanyAccess();
  const accountDetails = useAccountDetails();

  const hasDealerAccountAccess = useMemo(() => {
    const { dealer } = accountDetails || {};
    return hasDealerAccess ? dealer : false;
  }, [hasDealerAccess, accountDetails]);

  useEffect(() => {
    socketService.connect((state) => {
      if (state === StompSocketState.OPEN) {
        setConnected(true);
      }
    });
  }, []);

  useAccountSwitchedSnackbar(connected);

  if (!connected) {
    return <UILoader absolute />;
  }

  return (
    <StyledSecuredRoutes>
      <StyledSecuredRoutesScreens>
        <Routes>
          <Route path={APP_ROUTES.HOME} element={<MainScreen />}>
            <Route index={true} element={<Navigate to={SECURED_ROUTES.TRADE} replace={true} />} />
            <Route path={SECURED_ROUTES.TRADE} element={<TradeScreen />} />
            <Route path={SECURED_ROUTES.MARKETS} element={<MarketsScreen />} />
            <Route path={SECURED_ROUTES.ASSETS} element={<AssetsScreen />} />
            <Route path={SECURED_ROUTES.SIGNAL} element={<SignalScreen />} />
            {hasNewsAccess && <Route path={SECURED_ROUTES.NEWS} element={<NewsScreen />} />}
            {hasDealerAccountAccess && <Route path={SECURED_ROUTES.DEALER} element={<DealerScreen />} />}
            {hasCalendarAccess && <Route path={SECURED_ROUTES.CALENDAR} element={<CalendarScreen />} />}
            {hasEducationalVideo && <Route path={SECURED_ROUTES.EDUCATION} element={<EducationScreen />} />}
          </Route>
          <Route path="*" element={<Navigate to={SECURED_ROUTES.TRADE} replace={true} />} />
        </Routes>
      </StyledSecuredRoutesScreens>
    </StyledSecuredRoutes>
  );
};

export default SecuredRoutes;

const StyledSecuredRoutes = styled.div`
  flex: 1;
  flex-direction: row;
`;

const StyledSecuredRoutesScreens = styled.div`
  flex: 1;
  height: 100%;
`;
